import {chakra} from '@chakra-ui/react'
import {PatientTherapySequence} from '@cypro/engine/schema'
import {getMonthDayFormatter} from '@cypro/ui/utils'
import {isSameDay, startOfToday} from 'date-fns'
import {useCallback} from 'react'

const toDayKey = (value: number) => {
  const d = new Date(value)
  return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`
}

/**
 * Groups rows by therapy date and renders a day header for each group. When `enabled` is false (e.g. a date filter is active), no day headers are rendered.
 */
export function useTherapyDayHeaders(enabled: boolean) {
  const isHeaderRow = useCallback(
    (current: PatientTherapySequence, previous: PatientTherapySequence | undefined) => {
      if (!enabled) return false
      if (!previous) return true
      return toDayKey(current.therapyDate) !== toDayKey(previous.therapyDate)
    },
    [enabled]
  )

  const renderHeader = useCallback(
    (rowData: PatientTherapySequence) => {
      const currentDate = startOfToday()
      const isCurrentDate = isSameDay(new Date(rowData.therapyDate), currentDate)

      return (
        <chakra.div py="2" borderBottomWidth="1px" borderColor="border" bg={isCurrentDate ? 'cyan.subtle' : undefined}>
          {isCurrentDate && <chakra.div position="absolute" left="0" top="0" bottom="0" width="2px" bg="cyan.solid" />}

          <chakra.span
            display="block"
            ml="10"
            px="3"
            py="1"
            borderRadius="4xl"
            borderWidth="1px"
            borderColor={isCurrentDate ? 'cyan.focusRing' : 'border.muted'}
            bg={isCurrentDate ? 'cyan.subtle' : 'bg.muted'}
            color={isCurrentDate ? 'cyan.fg' : 'text.fg'}
            textStyle="sm"
            fontWeight="medium"
            width="max-content"
          >
            {getMonthDayFormatter().format(rowData.therapyDate)}
          </chakra.span>
        </chakra.div>
      )
    },
    []
  )

  const highlightRow = useCallback(
    (rowData: PatientTherapySequence) => {
      return enabled && isSameDay(new Date(rowData.therapyDate), startOfToday())
    },
    [enabled]
  )

  return {isHeaderRow, renderHeader, highlightRow}
}
